// Global keyboard handler for application shortcuts

/**
 * Keyboard shortcut definition.
 */
export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  description: string;
  handler: () => void;
}

/**
 * Global keyboard handler for application shortcuts.
 * Manages keyboard event listeners and shortcut registration.
 */
export class KeyboardHandler {
  private shortcuts: Map<string, KeyboardShortcut> = new Map();
  private enabled: boolean = true;
  private boundHandleKeyDown: (e: KeyboardEvent) => void;

  constructor() {
    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    document.addEventListener("keydown", this.boundHandleKeyDown);
  }

  /**
   * Register a keyboard shortcut.
   */
  register(shortcut: KeyboardShortcut): void {
    const key = this.makeKey(shortcut);
    this.shortcuts.set(key, shortcut);
  }

  /**
   * Unregister a shortcut.
   */
  unregister(key: string, ctrl?: boolean, shift?: boolean, alt?: boolean): void {
    const shortcutKey = this.makeKey({
      key,
      ctrl,
      shift,
      alt,
      description: "",
      handler: () => {},
    });
    this.shortcuts.delete(shortcutKey);
  }

  /**
   * Enable/disable all shortcuts.
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Check if shortcuts are currently enabled.
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Create a unique key string for a shortcut.
   */
  private makeKey(shortcut: KeyboardShortcut): string {
    const parts: string[] = [];
    if (shortcut.ctrl) parts.push("ctrl");
    if (shortcut.shift) parts.push("shift");
    if (shortcut.alt) parts.push("alt");
    parts.push(shortcut.key.toLowerCase());
    return parts.join("+");
  }

  /**
   * Handle keydown events.
   */
  private handleKeyDown(e: KeyboardEvent): void {
    // Ignore if disabled
    if (!this.enabled) return;

    // Ignore if focus is in an input element
    if (
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement
    ) {
      return;
    }

    // Also ignore if target has contenteditable
    if (e.target instanceof HTMLElement && e.target.isContentEditable) {
      return;
    }

    const key = this.makeKey({
      key: e.key,
      ctrl: e.ctrlKey || e.metaKey, // Support Cmd on Mac
      shift: e.shiftKey,
      alt: e.altKey,
      description: "",
      handler: () => {},
    });

    const shortcut = this.shortcuts.get(key);
    if (shortcut) {
      e.preventDefault();
      e.stopPropagation();
      shortcut.handler();
    }
  }

  /**
   * Get all registered shortcuts (for help display).
   */
  getShortcuts(): KeyboardShortcut[] {
    return Array.from(this.shortcuts.values());
  }

  /**
   * Clean up event listeners.
   */
  destroy(): void {
    document.removeEventListener("keydown", this.boundHandleKeyDown);
    this.shortcuts.clear();
  }
}
